<template>
  <div class="is-flex is-flex-wrap-wrap is-relative is-full-width" ref="container">
    <div
      class="is-flex is-flex-wrap-wrap overflow-hidden list-items"
      :class="{ expanded: isExpanded }"
    >
      <template v-for="(item, index) in items" :key="`list-item-${index}`">
        <span
          class="list-item"
          :test-item-is-visible="(index < visibleItemsCount && !isExpanded) || isExpanded"
          :class="{ 'item-is-visible': (index < visibleItemsCount && !isExpanded) || isExpanded }"
          :data-e2e="ExpandableIndicatorListCypressAttribute.views.LIST_ITEM"
        >
          <slot name="item" :item="item" :index="index">
            {{ item }}
          </slot>
        </span>

        <span
          v-if="index === visibleItemsCount - 1 && hiddenItemsCount > 0 && !isExpanded"
          class="has-text-info-dark is-clickable expand-button"
          :key="`expand-button-${index}`"
          :data-e2e="ExpandableIndicatorListCypressAttribute.buttons.EXPAND_BUTTON"
          @click.stop="toggleExpand"
        >
          <slot name="expand-indicator" :hidden-items-count="hiddenItemsCount">
            +{{ hiddenItemsCount }}
          </slot>
        </span>
      </template>
      <span
        v-if="isExpanded"
        class="is-clickable reduce-button"
        :data-e2e="ExpandableIndicatorListCypressAttribute.buttons.REDUCE_BUTTON"
        @click.stop="toggleExpand"
      >
        <slot name="reduce-indicator">
          <span class="is-underlined">{{ $t('core.show-less') }}</span>
        </slot>
      </span>
    </div>
  </div>
</template>

<script>
import { ExpandableIndicatorListCypressAttribute } from '@/modules/core/app/constants/ExpandableIndicatorListCypressAttribute';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ExpandableIndicatorList',
  slotsDefinition: {
    item: { item: String, index: Number },
    expandIndicator: { hiddenItemsCount: Number },
    reduceIndicator: {}
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    field: {
      type: String,
      default: 'name'
    }
  },
  static: {
    ExpandableIndicatorListCypressAttribute
  },
  data() {
    return {
      isExpanded: false,
      visibleItemsCount: 0
    };
  },
  computed: {
    items() {
      return this.data.map((item) => item[this.field]);
    },
    hiddenItemsCount() {
      return this.items.length - this.visibleItemsCount;
    }
  },
  methods: {
    calculateVisibleItems() {
      this.$nextTick(() => {
        const container = this.$refs.container;

        const items = Array.from(container.querySelectorAll('.list-items > :not(.expand-button)'));

        this.visibleItemsCount = this.countVisibleItems(container, items);
      });
    },
    countVisibleItems(container, items) {
      const TOLERANCE = 30;

      return items.reduce(
        ({ visibleCount, totalWidth }, item) => {
          const itemWidth = this.getItemRealWidth(item);

          totalWidth += itemWidth;

          const isOverflowing = totalWidth > container.clientWidth - TOLERANCE;
          if (isOverflowing) {
            return { visibleCount, totalWidth };
          }

          return { visibleCount: visibleCount + 1, totalWidth };
        },
        { visibleCount: 0, totalWidth: 0 }
      ).visibleCount;
    },
    getItemRealWidth(item) {
      const itemStyles = window.getComputedStyle(item);
      const itemMarginRight = parseFloat(itemStyles.marginRight);
      const itemMarginLeft = parseFloat(itemStyles.marginLeft);
      const itemMargin = itemMarginRight + itemMarginLeft;

      return item.offsetWidth + itemMargin;
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    }
  },
  mounted() {
    this.calculateVisibleItems();
    window.addEventListener('resize', this.calculateVisibleItems);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateVisibleItems);
  }
};
</script>

<style scoped>
.list-items {
  max-height: var(--ce-32px);
  transition: max-height 0.3s ease;
}

.list-items.expanded {
  max-height: none;
}

.list-item,
.expand-button,
.reduce-button {
  margin: var(--ce-3px) var(--ce-3px) var(--ce-3px) var(--ce-3px);
}
</style>
